// Wrapper fino sobre fetch.
// Auth: cookie cm_session HttpOnly emitida por /api/auth/workos/callback. La
// usamos via `credentials: 'include'`. No hay Bearer token en sessionStorage —
// el JS del panel no puede acceder a la cookie HttpOnly (cross-XSS guard).
//
// El bearer del placeholder anterior queda como fallback DEV únicamente: si
// estás en localhost contra un API distinto y configurás un token manual
// para sortear CORS, usalo desde sessionStorage. Producción ignora el bearer.

const BASE_URL = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function devBearer(): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    const token = sessionStorage.getItem("cm:dev-bearer");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  return {};
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...devBearer(),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    // 401 → redirect a login. Centralizado acá para no repetir en cada hook.
    if (res.status === 401 && typeof window !== "undefined") {
      // Evitamos loop si ya estamos en /login.
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    throw new ApiError(res.status, body, `API ${res.status} ${path}`);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiGet = <T>(path: string) => api<T>(path, { method: "GET" });
export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined });
export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined });
export const apiDelete = <T>(path: string) => api<T>(path, { method: "DELETE" });
